import { canFarmerHaveNoEmail, Farmer, FarmerData } from 'modules/domain/farmer/types'
import React, { useEffect, useMemo, useState } from 'react'
import { ModalProps } from '@agro-club/frontend-shared'
import { Progress } from 'modules/types'
import * as Styled from './styled'
import styled from 'styled-components'
import CustomerExists from './CustomerExists'
import PhoneForm from './PhoneForm'
import { AddCustomerForm, FulfillCustomerForm } from 'views/components/CustomerModal/CustomerForm'
import RegionsModal from 'views/components/PickRegionsForm/RegionsModal'
import { useFarmersByPhoneMatches } from 'modules/domain/farmer/hooks'
import { CountryCode } from 'libphonenumber-js'
import { useSelector } from 'react-redux'
import ConfigSelectors from 'modules/domain/config/selectors'
import { Status } from 'types/entities'
import AuthSelectors from 'modules/domain/auth/selectors'
import * as farmerManagers from 'modules/domain/farmer/managers'
import { FarmerOrderStatus } from 'types/farmerOrder'

type CustomerModalProps = {
  onCustomerSelect: (id: string, customer: Farmer) => void
  hideFarmerInfoChecking?: boolean
}

const CustomerFormWrapper = styled.div`
  padding-top: 18px;
`

const MatchesList = styled.div`
  margin: 0 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MatchOption = styled.button<{ selected: boolean }>`
  text-align: left;
  border: 1px solid ${({ selected, theme }) => (selected ? theme.color.primary500 : theme.color.onSurfaceLowEmphasys)};
  background: ${({ selected, theme }) => (selected ? theme.color.primary100 : theme.color.backgroundPrimary)};
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
`

const MatchTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.onPrimaryDark};
`

const MatchSubtitle = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.onSurfaceMidEmphasys};
`

const isProfileFulfilled = (profile: Farmer): boolean => {
  const hasName = () => {
    return !!profile.first_name && !!profile.last_name
  }
  const hasPhoneAndEmail = () => {
    return !!profile.phone_number && (!!profile.email || canFarmerHaveNoEmail(profile))
  }
  const hasAddress = () => {
    return !!profile.legal_address
  }

  return hasName() && hasPhoneAndEmail() && hasAddress()
}

export const CustomerForm: React.FC<{
  phone: string
  countryCode: CountryCode
  onBack: () => void
  onSelect: (farmer: FarmerData) => void
  onCancel: () => void
  hideFarmerInfoChecking?: boolean
}> = ({ phone, countryCode, onBack, onSelect, onCancel, hideFarmerInfoChecking = false }) => {
  const [progress, farmers = []] = useFarmersByPhoneMatches(!phone, phone)
  const [selectedFarmerId, setSelectedFarmerId] = useState<string>()
  const [ordersCountByFarmerId, setOrdersCountByFarmerId] = useState<Record<string, number>>({})
  const userCompany = useSelector(AuthSelectors.userCompany)

  useEffect(() => {
    if (farmers.length) {
      setSelectedFarmerId(farmers[0].id)
      return
    }
    setSelectedFarmerId(undefined)
  }, [farmers])

  useEffect(() => {
    let canceled = false

    if (!hideFarmerInfoChecking || !farmers.length || !userCompany?.id) {
      setOrdersCountByFarmerId({})
      return () => {
        canceled = true
      }
    }

    const fetchOrdersCounts = async () => {
      const values = await Promise.all(
        farmers.map(async (item): Promise<[string, number]> => {
          try {
            const count = await farmerManagers.farmerOrdersCount({
              owner_id: item.id,
              distributor_id: userCompany.id,
              producer_id: userCompany.producers_relations,
              status: FarmerOrderStatus.Confirmed,
            })
            return [item.id, Number(count) || 0]
          } catch {
            return [item.id, 0]
          }
        }),
      )

      if (!canceled) {
        setOrdersCountByFarmerId(Object.fromEntries(values))
      }
    }

    fetchOrdersCounts()

    return () => {
      canceled = true
    }
  }, [farmers, hideFarmerInfoChecking, userCompany])

  const farmer = useMemo(() => {
    if (!farmers.length) return undefined
    const selectedFarmer = !selectedFarmerId
      ? farmers[0]
      : farmers.find((item) => item.id === selectedFarmerId) || farmers[0]

    const ordersCount = ordersCountByFarmerId[selectedFarmer.id]
    if (typeof ordersCount === 'number') {
      return {
        ...selectedFarmer,
        ordersCount,
      }
    }

    return selectedFarmer
  }, [farmers, ordersCountByFarmerId, selectedFarmerId])

  if ([Progress.IDLE, Progress.WORK].includes(progress)) {
    return <Styled.Spinner />
  }

  let content: React.ReactNode = null

  if (progress === Progress.SUCCESS && farmer && isProfileFulfilled(farmer)) {
    const isFarmerActive = farmer.status === Status.Active

    content = (
      <CustomerExists
        farmer={farmer}
        onSelect={() => onSelect(farmer)}
        onCancel={onCancel}
        hideFarmerInfoChecking={hideFarmerInfoChecking}
        isFarmerActive={isFarmerActive}
      />
    )
  } else if (farmer && !isProfileFulfilled(farmer)) {
    content = (
      <FulfillCustomerForm
        id={farmer.id}
        farmer={farmer}
        phone={phone}
        onCancel={onCancel}
        onSelect={onSelect}
        countryCode={countryCode}
      />
    )
  } else {
    content = <AddCustomerForm phone={phone} countryCode={countryCode} onCancel={onCancel} onSelect={onSelect} />
  }

  return (
    <CustomerFormWrapper data-test-id={'modal-customer-form'}>
      <Styled.BackBtn onClick={onBack} />
      {progress === Progress.SUCCESS && farmers.length > 1 && (
        <MatchesList data-test-id={'customer-match-list'}>
          {farmers.map((item) => {
            const fullName = [item.first_name, item.last_name].filter(Boolean).join(' ').trim() || item.phone_number
            return (
              <MatchOption
                key={item.id}
                type="button"
                selected={item.id === farmer?.id}
                onClick={() => setSelectedFarmerId(item.id)}
              >
                <MatchTitle>{fullName}</MatchTitle>
                <MatchSubtitle>{item.phone_number}</MatchSubtitle>
              </MatchOption>
            )
          })}
        </MatchesList>
      )}
      {content}
    </CustomerFormWrapper>
  )
}

const CustomerModal: React.FC<ModalProps & CustomerModalProps> = ({
  isOpen,
  onClose,
  onCustomerSelect,
  hideFarmerInfoChecking = false,
}) => {
  const [phone, setPhone] = useState('')

  const [countryCode, setCountryCode] = useState<CountryCode>('CA')
  const setPhoneAndCountryCode = (phone: string, countryCode: CountryCode) => {
    setPhone(phone)
    setCountryCode(countryCode)
  }

  const wlConfig = useSelector(ConfigSelectors.config)
  const phoneCodes = wlConfig?.phone_codes || []

  return (
    <RegionsModal isOpen={isOpen} onClose={onClose}>
      {!phone ? (
        <PhoneForm onNext={setPhoneAndCountryCode} phoneCodes={phoneCodes} />
      ) : (
        <CustomerForm
          phone={phone}
          countryCode={countryCode}
          onSelect={(farmer: FarmerData) => {
            onCustomerSelect(farmer.id, farmer)
            setPhone('')
            onClose()
          }}
          onBack={() => setPhone('')}
          onCancel={() => {
            onClose()
            setPhone('')
          }}
          hideFarmerInfoChecking={hideFarmerInfoChecking}
        />
      )}
    </RegionsModal>
  )
}

export default CustomerModal
